import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, FileText, Trash2, Layout, Search, Book, Calendar, User } from 'lucide-react';
import ScheduleBanner from './ScheduleBanner';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';

const formatSchedule = (schedule) => {
  if (!schedule) return 'No schedule set';
  try {
    const scheduleData = typeof schedule === 'string' ? JSON.parse(schedule) : schedule;
    return scheduleData.displayString || scheduleData.raw || schedule;
  } catch (error) {
    console.error('Error parsing schedule:', error);
    return schedule; // Return raw schedule if parsing fails
  }
};

const LectureList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const [activeTab, setActiveTab] = useState('all');
  const [sharedLectures, setSharedLectures] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    let isMounted = true;

    const fetchClassData = async () => {
      if (!isLoaded || !isSignedIn || !id) return;
      
      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes/${id}`
        );
        const data = await response.json();
        
        if (isMounted) {
          setClassData(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching class:', error);
          setError('Failed to load class. Please try again later.');
        }
      }
    };

    fetchClassData();

    return () => {
      isMounted = false;
    };
  }, [id, isLoaded, isSignedIn]);

  useEffect(() => {
    let isMounted = true;

    const fetchLectures = async () => {
      if (!isLoaded || !isSignedIn || !id || !classData) return;
      
      try {
        setIsLoading(true);
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${id}`
        );
        const data = await response.json();
        
        const regular = data.filter(lecture => !lecture.is_shared);
        const shared = data.filter(lecture => lecture.is_shared);
        
        setLectures(regular);
        setSharedLectures(shared);
        setError(null);
      } catch (error) {
        console.error('Error fetching lectures:', error);
        setError('Failed to load lectures. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLectures();

    return () => {
      isMounted = false;
    };
  }, [id, isLoaded, isSignedIn, classData]);

  useEffect(() => {
    const fetchSharedLectures = async () => {
      try {
        const response = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/shared'
        );
        const data = await response.json();
        console.log('Shared lectures:', data);
        setSharedLectures(data);
      } catch (error) {
        console.error('Error fetching shared lectures:', error);
      }
    };

    if (activeTab === 'shared') {
      fetchSharedLectures();
    }
  }, [activeTab]);

  if (!isLoaded) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!classData) {
    return <div>Loading class data...</div>;
  }

  const handleDelete = async (e, lectureId) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this lecture?')) {
      try {
        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${id}/${lectureId}`,
          { method: 'DELETE' }
        );
        
        if (!response.ok) {
          throw new Error('Failed to delete lecture');
        }
        
        setLectures(lectures.filter(lecture => lecture.id !== lectureId));
      } catch (error) {
        console.error('Error deleting lecture:', error);
        alert('Failed to delete lecture');
      }
    }
  };

  const filteredLectures = lectures.filter(lecture =>
    lecture.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-theme theme-transition">
      <ScheduleBanner 
        classes={[]} 
        onNavigate={(classId) => navigate(`/class/${classId}/lectures`)}
      />

      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm mb-4 overflow-x-auto">
            <button
              onClick={() => navigate('/classes')}
              className="text-accent hover:text-theme hover:underline whitespace-nowrap"
            >
              Classes
            </button>
            <span className="mx-2 text-accent">/</span>
            <span className="text-theme truncate">
              {classData?.title || '...'}
            </span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-theme truncate">
                {classData?.title || 'Loading...'}
              </h1>
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            <button
              onClick={() => navigate(`/class/${id}/new-lecture`)}
              className="px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-theme transform transition-all duration-200 hover:scale-[1.02] w-full sm:w-auto"
            >
              <Plus size={18} />
              New Lecture
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-lg flex items-center gap-3 bg-secondary border border-theme">
              <Calendar size={20} className="text-accent flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-accent">Schedule</p>
                <p className="font-medium text-theme truncate">
                  {formatSchedule(classData.schedule)}
                </p>
              </div>
            </div>
            <div className="p-4 rounded-lg flex items-center gap-3 bg-secondary border border-theme">
              <User size={20} className="text-accent flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-accent">Professor</p>
                <p className="font-medium text-theme truncate">
                  {classData.professor}
                </p>
              </div>
            </div>
            <div className="p-4 rounded-lg flex items-center gap-3 bg-secondary border border-theme">
              <Book size={20} className="text-accent flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-accent">Total Lectures</p>
                <p className="font-medium text-theme">
                  {lectures.length}
                </p>
              </div>
            </div>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Search lectures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 pl-10 rounded-lg bg-secondary text-theme border border-theme focus:outline-none focus:ring-2 focus:ring-primary transition-all"
            />
            <Search className="absolute left-3 top-3.5 text-accent" size={18} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <div className="flex space-x-4 border-b border-theme overflow-x-auto pb-1">
            <button
              onClick={() => setActiveTab('all')}
              className={`py-2 px-4 text-theme whitespace-nowrap ${activeTab === 'all' ? 'border-b-2 border-primary' : ''}`}
            >
              All Lectures
            </button>
            <button
              onClick={() => setActiveTab('shared')}
              className={`py-2 px-4 text-theme whitespace-nowrap ${activeTab === 'shared' ? 'border-b-2 border-primary' : ''}`}
            >
              Shared with Me
            </button>
          </div>
        </div>

        {activeTab === 'shared' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {sharedLectures.map((lecture) => (
              <div key={lecture.id} className="p-4 sm:p-6 rounded-lg bg-theme border border-theme">
                <div className="text-sm text-accent truncate">
                  Shared by: {lecture.shared_by_email}
                </div>
                <div className="text-sm text-accent truncate">
                  Original Class: {lecture.original_class_title}
                </div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-accent" />
                    <div className={`w-2 h-2 rounded-full ${
                      lecture.status === 'processing' ? 'bg-yellow-400' :
                      lecture.status === 'processed' ? 'bg-green-400' :
                      lecture.status === 'failed' ? 'bg-red-400' : 
                      'bg-gray-400'
                    }`} />
                  </div>
                </div>
                
                <h2 className="text-lg font-semibold mb-2 text-theme truncate">
                  {lecture.title}
                </h2>
                
                <p className="text-sm text-accent truncate">
                  From class: {lecture.class_title}
                </p>
                
                <p className="text-sm text-accent">
                  {new Date(lecture.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredLectures.map((lecture) => (
              <div
                key={lecture.id}
                onClick={() => navigate(`/class/${id}/lecture/${lecture.id}`, { 
                  state: { className: classData.title } 
                })}
                className="p-4 sm:p-6 rounded-lg cursor-pointer group relative bg-theme hover:bg-secondary border border-theme transform transition-all duration-200 hover:scale-[1.02] theme-transition"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-accent" />
                    <div className={`w-2 h-2 rounded-full ${
                      lecture.status === 'processing' ? 'bg-yellow-400' :
                      lecture.status === 'processed' ? 'bg-green-400' :
                      lecture.status === 'failed' ? 'bg-red-400' : 
                      'bg-gray-400'
                    }`} />
                  </div>
                  <button
                    onClick={(e) => handleDelete(e, lecture.id)}
                    className="p-2 rounded-lg opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-100 hover:text-red-600 text-accent"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
                
                <h2 className="text-lg font-semibold mb-2 text-theme truncate">
                  {lecture.title}
                </h2>
                
                <p className="text-sm text-accent">
                  {new Date(lecture.date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            ))}
          </div>
        )}

        {filteredLectures.length === 0 && (
          <div className="text-center py-12">
            <p className="text-lg text-accent">
              No lectures found. Add a new lecture to get started!
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default LectureList;